import _ from 'lodash';
import moment from 'moment';

import {
  BalanceOwnerTypes,
  DisbursementCauses,
}             from 'app/constants';
import format from 'app/helpers/format';

import env from 'server/env';
import csv from 'server/helpers/csv';

const nteeCodeObject = (nteeCode) => {
  if (!nteeCode) return null;
  return _.pick(nteeCode, ['code', 'name', 'cta', 'tags', 'nonprofitCount', 'category']);
}

const pendingClaims = (nonprofitAdmins) => {
  return {
    nonprofitAdmins: nonprofitAdmins.map((npa) => ({
      id: npa.id,
      createdAt: npa.createdAt,
      user: {
        id: npa.user.id,
        name: npa.user.name,
        email: npa.user.email,
        city: npa.user.city,
        state: npa.user.state,
        postalCode: npa.user.postalCode,
      },
      nonprofit: npa.nonprofit.serialized,
      nonprofitProfile: {
        id: npa.nonprofitProfile.id,
        updatedAt: npa.nonprofitProfile.updatedAt,
      },
    })),
  };
};

const slzrDisbursements = ({rows, summary, pagination, nonprofit, employee, company, user}) => {
  const entNames = ['company', 'employee', 'user', 'nonprofit', 'bracket', 'fCampaign'];
  return {
    pagination,
    summary,
    nonprofit: nonprofit ? _.pick(nonprofit, ['id', 'ein', 'name', 'countryCode', 'registeredNumber']) : null,
    company:   company   ? _.pick(company,   ['id', 'name', 'slug']) : null,
    employee:  employee  ? _.pick(employee,  ['id', 'firstName', 'lastName', 'email']) : null,
    user:      user      ? _.pick(user,      ['id', 'firstName', 'lastName', 'email']) : null,
    disbursements: rows.map((row) => {
      const obj = {};
      Object.entries(row).forEach(([key, val]) => {
        const [entity, attr] = key.split('_');
        if (entNames.includes(entity)) {
          if (!obj[entity]) obj[entity] = {};
          obj[entity][attr] = val;
        } else {
          obj[key] = val;
        }
      });
      entNames.forEach((entName) => {
        if (!obj[entName].id) obj[entName] = null;
      });
      return obj;
    }),
  };
};

const slzrDisbursementsCsv = ({rows}) => {
  const lines = ['millieId,dafId,createdAt,sentToDafAt,daf,amount,actualAmount,nonprofitMillieId,nonprofitName,nonprofitEin,nonprofitCountryCode,nonprofitRegisteredNumber,nonprofitPercentId,percentPaymentStatus,percentPaymentMethod,percentPaymentInitiatedAt,percentPaidAt,isAnonymous,causeType,causeId,note,companyId,companyName,employeeId,employeeEmail,userId,userEmail,bracketId,bracketName'];
  rows.forEach((row) => {
    const line = [
      row.id,
      csv.quote(row.dafId),
      csv.timestamp(row.createdAt),
      csv.timestamp(row.disbursedAt),
      csv.quote(row.daf),
      csv.usd(row.amountInCents),
      csv.usd(row.actualAmountInCents),
      csv.quote(row.nonprofit_id),
      csv.quote(row.nonprofit_name),
      csv.ein(row.nonprofit_ein),
      csv.quote(row.nonprofit_countryCode),
      csv.quote(row.nonprofit_registeredNumber),
      csv.quote(row.nonprofit_percentId),
      csv.quote(row.percentStatus),
      csv.quote(row.percentPaymentMethod),
      csv.timestamp(row.percentPaymentInitiatedAt),
      csv.timestamp(row.percentPaidAt),
      csv.bool(row.isAnonymous),
      csv.quote(row.causeType),
      csv.quote(row.causeId),
      csv.quote(row.note),
      csv.quote(row.company_id),
      csv.quote(row.company_name),
      csv.quote(row.employee_id),
      csv.quote(row.employee_email),
      csv.quote(row.user_id),
      csv.quote(row.user_email),
      csv.quote(row.bracket_id),
      csv.quote(row.bracket_name),
    ];
    lines.push(line.join(','));
  });
  return lines.join(`\n`);
};

const slzrDisbursement = ({disbursement: disb}) => {
  return {disb};
};

const nonprofitProfiles = (nonprofits, pagination) => {
  return {
    nonprofits: nonprofits.map((nonprofit) => ({
      id: nonprofit.id,
      name: nonprofit.name,
      ein: format.ein(nonprofit.ein),
      city: nonprofit.city,
      state: nonprofit.state,
      postalCode: nonprofit.postalCode,
      claimed: nonprofit.claimed,
      activeProfileId: nonprofit.activeProfileId,
      admins: nonprofit.admins.map((admin) => ({
        id: admin.id,
        name: admin.name,
        email: admin.email,
        city: admin.city,
        state: admin.state,
        postalCode: admin.postalCode,
      })),
    })),
    pagination,
  };
};

const userObj = (user) => {
  const employerCompany = user.employerCompanies?.[0];
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    emailVerified: user.emailVerified,
    createdAt: user.createdAt,
    joinDate: moment(user.createdAt).format('YYYY-MM-DD'),
    city: user.city,
    state: user.state,
    backstageAccess: !!user.backstageRole,
    backstageRole: user.backstageRole,
    donationCount: user.donationCount,
    activeRecurringDonationCount: user.activeRecurringDonationCount,
    avatarUrl: user.avatarUrl,
    postalCode: user.postalCode,
    stripeCustomerId: user.stripeCustomerId,
    employerCompany: employerCompany ? {
      id: employerCompany.id,
      name: employerCompany.name,
    } : null,
  };
};

const slzrUsers = (users, pagination) => {
  return {
    pagination,
    users: users.map(userObj),
  };
};

const slzrUser = ({user}) => {
  return {
    user: userObj(user),
  };
};

const collections = (collectionObjs) => {
  return {
    collections: collectionObjs.map((collection) => {
      return {
        id: collection.id,
        title: collection.title,
        path: collection.path,
        description: collection.description,
        order: collection.order,
        published: collection.published,
        madness: collection.madness,
        nonprofits: collection.nonprofits.map((nonprofit) => {
          return {
            collectionNonprofitId: nonprofit.CollectionNonprofit.id,
            collectionNonprofitOrder: nonprofit.CollectionNonprofit.order,
            ...nonprofit.serialized,

          };
        }),
      };
    }),
  };
};

const slzrNonprofit = (nonprofit) => {
  const npObj = {
    id: nonprofit.id,
    name: nonprofit.name,
    mission: nonprofit.mission,
    nteeCode: nonprofit.nteeCode,
    city: nonprofit.city,
    state: nonprofit.state,
    postalCode: nonprofit.postalCode,
    latitude: nonprofit.latitude,
    longitude: nonprofit.longitude,
    ein: nonprofit.ein ? format.ein(nonprofit.ein) : null,
    activeProfileId: nonprofit.activeProfileId,
    budgetTotal: nonprofit.budgetTotal,
    budgetYear: nonprofit.budgetYear,
    budgetPercentProgram: nonprofit.budgetPercentProgram,
    budgetPercentManagement: nonprofit.budgetPercentManagement,
    budgetPercentFundraising: nonprofit.budgetPercentFundraising,
    imgUrl: nonprofit.imgUrl,
    claimed: nonprofit.claimed,
    disabled: nonprofit.disabled,
    badges: nonprofit.badges,
    countries: nonprofit.countries,
    formationYear: nonprofit.formationYear,
    ineligibleReason: nonprofit.ineligibleReason,
    lastRefreshAt: nonprofit.lastRefreshAt,
    address: nonprofit.address,
    website: nonprofit.website,
    subsection: nonprofit.subsection,
    registeredNumber: nonprofit.registeredNumber,
    countryCode: nonprofit.countryCode,
    percentId: nonprofit.percentIdSafe,
    percentOrgCount: nonprofit.percentOrgCount,
    percentOrgsLastSyncAt: nonprofit.percentOrgsLastSyncAt,
    percentClaimed: nonprofit.percentClaimed,
    percentLastSyncAt: nonprofit.percentLastSyncAt,
    altNames: nonprofit.altNames,
  };
  if (nonprofit.nteeCodes) {
    const nteeObj = nonprofit.nteeCodes.find(no => no.code === nonprofit.nteeCode.substring(0, 3));
    npObj.nteeObj = nteeCodeObject(nteeObj);
  }
  if (nonprofit.admins) {
    npObj.admins = nonprofit.admins.map((admin) => {
      return {
        id: admin.id,
        name: admin.name,
        email: admin.email,
        avatarUrl: admin.avatarUrl,
      };
    });
  }
  if (nonprofit.registry) {
    npObj.registry = _.pick(nonprofit.registry, ['id', 'englishName']);
  }
  return {
    nonprofit: npObj,
  };
};

const nonprofits = (nonprofitObjs, pagination) => {
  return {
    nonprofits: nonprofitObjs.map((nonprofit) => {
      // const admin = nonprofit.admins[0] && {
      //   id: nonprofit.admins[0].id,
      //   name: nonprofit.admins[0].name,
      //   email: nonprofit.admins[0].email,
      //   city: nonprofit.admins[0].city,
      //   state: nonprofit.admins[0].state,
      //   postalCode: nonprofit.admins[0].postalCode,
      // };
      return {
        id: nonprofit.id,
        name: nonprofit.name,
        ein: nonprofit.ein ? format.ein(nonprofit.ein) : null,
        registeredNumber: nonprofit.registeredNumber,
        countryCode: nonprofit.countryCode,
        city: nonprofit.city,
        state: nonprofit.state,
        postalCode: nonprofit.postalCode,
        claimed: nonprofit.claimed,
        activeProfileId: nonprofit.activeProfileId,
        badges: nonprofit.badges,
        disabled: nonprofit.disabled,
        countries: nonprofit.countries,
        nteeCode: nonprofit.nteeCode,
        percentClaimed: nonprofit.percentClaimed,
      };
    }),
    pagination,
  };
};

const slzrCompanies = (companies) => {
  return {
    companies: companies.map((company) => {
      return {
        id: company.id,
        name: company.name,
        isLaunched: company.isLaunched,
        companyDashboardId: company.companyDashboardId,
        timezone: company.timezone,
        logoUrl: company.logoUrl,
        slug: company.slug,
        features: company.features,
        dtdUpdatedAt: company.dtdUpdatedAt,
        stripeCustomerId: company.stripeCustomerId,
        saasEmployeeCap: company.saasEmployeeCap,
        saasWhitelist: company.saasWhitelist,
        employeeCount: company.employeeCount,
        customerType: company.customerType,
        customerStatus: company.customerStatus,
        hasTeams: company.hasTeams,
      };
    }),
  };
};

const slzrCompany = (company) => {
  return {
    company: {
      id: company.id,
      name: company.name,
      isLaunched: company.isLaunched,
      companyDashboardId: company.companyDashboardId,
      timezone: company.timezone,
      logoUrl: company.logoUrl,
      slug: company.slug,
      features: company.features,
      dtdUpdatedAt: company.dtdUpdatedAt,
      stripeCustomerId: company.stripeCustomerId,
      saasEmployeeCap: company.saasEmployeeCap,
      saasWhitelist: company.saasWhitelist,
      source: company.source,
      employeeCount: company.employeeCount,
      customerType: company.customerType,
      customerStatus: company.customerStatus,
      hasTeams: company.hasTeams,
      year2020StartDate: company.year2020StartDate,
      currentYear: company.currentYear,
      currentYearStartDate: company.currentYearRange.startMoment,
      currentYearEndDate: company.currentYearRange.endMoment,
      customerGivingUser: company.customerGivingUser ? {
        id: company.customerGivingUser.id,
        email: company.customerGivingUser.email,
        firstName: company.customerGivingUser.firstName,
        lastName: company.customerGivingUser.lastName,
      } : undefined,
      employeeGivingUser: company.employeeGivingUser ? {
        id: company.employeeGivingUser.id,
        email: company.employeeGivingUser.email,
        firstName: company.employeeGivingUser.firstName,
        lastName: company.employeeGivingUser.lastName,
      } : undefined,
    },
  };
};

const LEGACY_WALLET_LOADS_COUNT = 223;
const LEGACY_WALLET_LOADS_TOTAL_IN_CENTS = 1181900;
const LEGACY_WALLET_LOADS_ACTUAL_TOTAL_IN_CENTS = 1088478;
const LEGACY_DISBURSEMENTS_TOTAL_IN_CENTS = 513100; // this is according to Sawyer's records
const LEGACY_DISBURSEMENTS_ACTUAL_TOTAL_IN_CENTS = 491662; // this is according to Givinga, should be closer to 472543 if sawer's records are correct
const lastLegacyDate = moment.utc('2019-10-31');
const getInt = (obj, path) => parseInt(_.get(obj, path) || 0);
const slzrDashboardOverview = ({walletLoads, companyCharges, groupedDisbursements, momentEnd, momentStart}) => {
  const completeDisbursements = groupedDisbursements.find(d => d.complete);
  const failedDisbursements = groupedDisbursements.find(d => !d.complete);
  const includeLegacy = !momentStart.isAfter(lastLegacyDate);
  return {
    startDate: momentStart.format('YYYY-MM-DD'),
    endDate: momentEnd.format('YYYY-MM-DD'),
    legacyUploads: {
      count: includeLegacy ? LEGACY_WALLET_LOADS_COUNT : 0,
      totalInCents: includeLegacy ? LEGACY_WALLET_LOADS_TOTAL_IN_CENTS : 0,
      actualTotalInCents: includeLegacy ? LEGACY_WALLET_LOADS_ACTUAL_TOTAL_IN_CENTS : 0,
    },
    legacyDisursements: {
      totalInCents: includeLegacy ? LEGACY_DISBURSEMENTS_TOTAL_IN_CENTS : 0,
      actualTotalInCents: includeLegacy ? LEGACY_DISBURSEMENTS_ACTUAL_TOTAL_IN_CENTS : 0,
    },
    walletLoads: {
      count: getInt(walletLoads, 'count'),
      totalInCents: getInt(walletLoads, 'totalInCents'),
      actualTotalInCents: getInt(walletLoads, 'actualTotalInCents'),
    },
    companyCharges: {
      count: getInt(companyCharges, 'count'),
      totalInCents: getInt(companyCharges, 'totalInCents'),
      actualTotalInCents: getInt(companyCharges, 'actualTotalInCents'),
    },
    completeDisbursements: {
      count: getInt(completeDisbursements, 'count'),
      totalInCents: getInt(completeDisbursements, 'totalInCents'),
      actualTotalInCents: getInt(completeDisbursements, 'actualTotalInCents'),
    },
    failedDisbursements: {
      count: getInt(failedDisbursements, 'count'),
      totalInCents: getInt(failedDisbursements, 'totalInCents'),
      actualTotalInCents: getInt(failedDisbursements, 'actualTotalInCents'),
    },
  };
};

const slzrFeatureFlag = (key, users, companies) => {
  const featureFlag = {
    key,
    users: users.map((user) => ({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    })),
    companies: companies.map((company) => ({
      id: company.id,
      name: company.name,
      slug: company.slug,
    })),
  };
  return {
    featureFlag,
  };
};

const slzrFailedCharges = (charges) => {
  return {
    charges: charges.map((charge) => {
      return {
        id: charge.id,
        amountInCents: charge.amountInCents,
        createdAt: charge.createdAt,
        stripeChargeId: charge.stripeChargeId,
        daf: charge.daf,
        stripeUrl: `https://dashboard.stripe.com/${env.stripeGivingaAccountId}/${env.millieEnv === 'production' ? '' : 'test/'}payments/${charge.stripeChargeId}`,
        company: {
          id: charge.company.id,
          slug: charge.company.slug,
          name: charge.company.name,
        },
      };
    }),
  };
};

const getBalanceOwnerName = (balance) => {
  if (balance.user) return balance.user.name;
  if (balance.company) return balance.company.name;
  if (balance.fund) return balance.fund.name;
  if (balance.nonprofit) return balance.nonprofit.name;
  if (balance.gift) return `Gift to ${balance.gift.recipientEmail}`;
  if (balance.companyPurchase) return `${balance.companyPurchase.type} Purchase, ${balance.companyPurchase.company.name}`;
  if (balance.ownerType === BalanceOwnerTypes.LEGACY_ADJUSTMENTS) return 'Legacy Adjustments';
  if (balance.ownerType === BalanceOwnerTypes.ADJUSTMENTS) return 'Adjustments';
};

const balanceObj = (balance) => {
  const obj = _.pick(balance, [
    'id', 'amount', 'actualAmount', 'ownerType', 'ownerId', 'createdAt',
    'totalIn', 'actualTotalIn', 'totalOut', 'actualTotalOut',
  ]);
  obj.ownerName = getBalanceOwnerName(balance);
  return obj;
};

const slzrBalances = ({balances, pagination, summary}) => {
  return {
    summary,
    pagination,
    balances: balances.map(balanceObj),
  };
};

const slzrBalance = ({balance}) => {
  return {
    balance: balanceObj(balance),
  };
};

const slzrBalanceTransactions = ({balance, transactions, pagination}) => {
  return {
    pagination,
    transactions: transactions.map((tx) => {
      const obj = _.pick(tx, ['id', 'createdAt', 'causeType', 'causeId', 'internalNotes', 'relatedBalanceTransactionId']);
      obj.isTo = tx.toBalanceId === balance.id;
      obj.description    = obj.isTo ? tx.toDescription : tx.fromDescription;
      obj.amount         = obj.isTo ? tx.amount        : (tx.amount * -1);
      obj.actualAmount   = obj.isTo ? tx.actualAmount  : (tx.actualAmount * -1);
      obj.otherBalanceId = obj.isTo ? tx.fromBalanceId : tx.toBalanceId;
      return obj;
    }),
  }
};

const slzrUnresolvedDisputes = ({disputes}) => {
  return {
    disputes: disputes.map((dispute) => {
      const disputeAttrs = ['id', 'stripeChargeId', 'stripeDisputeId', 'chargeType', 'chargeId', 'stripeObject', 'daf'];
      const wlAttrs = ['id', 'createdAt', 'amountInCents', 'actualAmountInCents', 'user.firstName', 'user.lastName', 'user.email', 'user.id', 'daf'];
      const ccAttrs = ['id', 'createdAt', 'amountInCents', 'actualAmountInCents', 'reason', 'stripePmType', 'company.id', 'company.slug', 'company.name', 'daf'];
      const obj = _.pick(dispute, disputeAttrs);
      obj.walletLoad = dispute.walletLoad ? _.pick(dispute.walletLoad, wlAttrs) : null;
      obj.companyCharge = dispute.companyCharge ? _.pick(dispute.companyCharge, ccAttrs) : null;
      return obj;
    }),
  };
};

const slzrNteeCodes = (nteeCodes) => {
  return {
    nteeCodes: nteeCodes.reduce((acc, nteeCode) => {
      acc[nteeCode.code] = nteeCodeObject(nteeCode);
      return acc;
    }, {}),
  };
};

const slzrNteeCode = (nteeCode) => {
  return {
    nteeCode: nteeCodeObject(nteeCode),
  };
};

const pvsObject = (pvs) => {
  return _.pick(pvs, ['id', 'percentId', 'percentUpdatedAt', 'createdAt', 'updatedAt', 'nonprofitName', 'status', 'percentData', 'millieData', 'registryId']);
};

const slzrPercentValidationSubmissions = (pvss) => {
  return {
    percentValidationSubmissions: pvss.map(pvsObject),
  };
};

const nonprofitRequestObj = (npr) => {
  const obj = _.pick(npr, ['registeredNumber', 'registryId', 'userCount', 'companyCount', 'newestCreatedAt', 'oldestCreatedAt', 'percentOrg', 'createdAt', 'registryName', 'percentClaimed']);
  if (npr.user) {
    obj.user = _.pick(npr.user, ['id', 'firstName', 'lastName', 'email']);
  }
  if (npr.company) {
    obj.company = _.pick(npr.company, ['id', 'name']);
  }
  if (npr.registry) {
    obj.registryName = npr.registry.name;
  }
  return obj;
}

const slzrNonprofitRequests = (nprs, pagination) => {
  return {
    pagination,
    nonprofitRequests: nprs.map(nonprofitRequestObj),
  };
};

export default {
  pendingClaims,
  nonprofitProfiles,
  users: slzrUsers,
  user: slzrUser,
  collections,
  nonprofit: slzrNonprofit,
  nonprofits,
  companies: slzrCompanies,
  company: slzrCompany,
  dashboardOverview: slzrDashboardOverview,
  featureFlag: slzrFeatureFlag,
  failedCharges: slzrFailedCharges,
  balances: slzrBalances,
  balance: slzrBalance,
  balanceTransactions: slzrBalanceTransactions,
  unresolvedDisputes: slzrUnresolvedDisputes,
  nteeCodes: slzrNteeCodes,
  nteeCode: slzrNteeCode,
  percentValidationSubmissions: slzrPercentValidationSubmissions,
  nonprofitRequests: slzrNonprofitRequests,
  disbursementsCsv: slzrDisbursementsCsv,
  disbursements: slzrDisbursements,
  disbursement: slzrDisbursement,
};
